import { AlertColor, Button, CircularProgress, Grid, Link, Modal, TextField, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import React, { useImperativeHandle } from 'react';
import { globalTheme } from '../../../common/theme';
import { SnackBarMessage, SnackBarMessageProps } from '../../component/SnackBarMessage';
import { useAppSelector } from '../../../common/hooks';
import { RootState } from '../../../common/Store';
import Title from '../../component/Title';
import { GeneralDialog } from '../../component/GeneralDialog';
import { Pad } from '../../../common/Formatter';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        margin: {
            margin: theme.spacing(1),
        },
        modalPaper: {
            position: 'absolute',
            width: 400,
            backgroundColor: theme.palette.background.paper,
            border: '2px solid #000',
            boxShadow: theme.shadows[5],
            padding: theme.spacing(2, 4, 3),
        },
        control: {
            marginBottom: theme.spacing(1)
        }
    }),

);

export interface InvoiceFilingRecordViewerHandle {
    Initialise: (region: string, year: number, month: number) => void,
}

export interface InvoiceFilingRecordViewerProps {
    onClose: Function;
}
  
export const InvoiceFilingRecordViewer = React.forwardRef((props: InvoiceFilingRecordViewerProps, ref: React.Ref<InvoiceFilingRecordViewerHandle>) => {
    const style = { ...useStyles(), ...globalTheme() };
    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState<any>();
    const [region, setRegion] = React.useState<string>();
    const [year, setYear] = React.useState<number>();
    const [month, setMonth] = React.useState<number>();

    //Dialogs and snacks
    const hasChanges = React.useRef<boolean>(false);
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }


    const [viewerUrl, setViewerUrl] = React.useState<string>("");

    useImperativeHandle(ref, () => ({

        Initialise(region: string, year: number, month: number) {
            setOpen(true);
            setLoading(true);
            hasChanges.current = false;

            setRegion(region);
            setYear(year);
            setMonth(month);
            GenerateReport(region, year, month);
        }

    }));

    function Export(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        let params = { region: region, invoiceYear: year, invoiceMonth: month};
        axios.get(`Api/V1/Invoice/FilingRecord/Export`, {
            params,
            headers: {
                'Content-Encoding': 'gzip'
            },
            responseType: 'blob'
        })
            .then((result) => {
                const file = new Blob([result.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;" });
  
                const url = window.URL.createObjectURL(file);
  
                const link = document.createElement("a");
                document.body.appendChild(link);
                link.href = url;
                link.download = (region ?? "") + year?.toString() + month?.toString().padStart(2, '0') + ".tsv";
                link.click();
  
                setLoading(false);
            },
                (error) => {
                    console.log(error);
                    setLoading(false);
                    showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                }
            )
    }
  
    function CloseModal() {
        setOpen(false);
    }

    function GenerateReport(selectedRegion: string, year: number, month: number) {
        let params = { region: selectedRegion, invoiceYear: year, invoiceMonth: month};
        axios.get('Api/V1/Invoice/FilingRecord/Preview', {
            params,
            headers: {
                'Content-Encoding': 'gzip'
            },
            responseType: 'blob'
        })
            .then((result) => {
                const file = new Blob([result.data], { type: "application/pdf" });

                const url = window.URL.createObjectURL(file);

                setViewerUrl(url);
                setLoading(false);
            },
            (error) => {
                console.log(error);
                setLoading(false);
                showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
            }
        );
    }

    return (
        <div>
            <Modal
                open={open}
             >
                <div className={style.ModalMain}>

                    {!loading ? (
                        <div style={{ height: '100%' }}>
                            <Title pageTitle={region ?? "" + year?.toString() + month?.toString().padStart(2, '0')} />

                            <div className={style.ModalHeading}>
                                <Typography variant="h4">{region ?? "" + year?.toString() + month?.toString().padStart(2, '0')}</Typography>
                            </div>

                            <div className={style.ModalContent} style={{height: '75vh'}}>
                                <iframe src={viewerUrl} width="100%" height="100%" style={{overflow: 'auto'}}>
                                </iframe>
                            </div>

                            <div className={style.ModalFooter}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className={style.margin}
                                    onClick={(e) => Export(e)}>Export</Button>
                                <Button
                                    variant="contained"
                                    className={style.margin}
                                    onClick={(e) => CloseModal()}>Close</Button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center' }}>
                            <CircularProgress color="primary" />
                        </div>
                    )}



                </div>
            </Modal>

            <SnackBarMessage {...snackSettings} />
        </div >
    );
});
